import { ChangeEvent, Fragment, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { deleteUrlsState, getCacheClearJobStatus, setAdaptiveType, setLike, setRegex } from './DeleteUrlsSlice';
import DeleteUrlsModal from './deleteUrlsModal/DeleteUrlsModal';
import { AdaptiveType } from '../UrlsState';
import { setIsOpened } from './deleteUrlsModal/DeleteUrlsModalSlice';
import { SliceStatus } from '../../BaseState';
import { CacheClearJobStatus } from './DeleteUrlsState';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RequireTier } from '../../auth/RequireTier';

interface DeleteUrlsProps {
  readonly userId: number;
}

const DeleteUrls = (props: DeleteUrlsProps) => {
  const state = useAppSelector(deleteUrlsState);
  const dispatch = useAppDispatch();

  const { userId } = props;

  const onLikeChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setLike(event.target.value));
  };

  const onRegexChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setRegex(event.target.value));
  };

  const openClearCacheModal = () => {
    dispatch(setIsOpened(true));
  };

  const onAdaptiveTypeChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
    dispatch(setAdaptiveType(AdaptiveType[event.target.value as keyof typeof AdaptiveType]));
  };

  useEffect(() => {
    dispatch(getCacheClearJobStatus({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (state.cacheClearJobStatus === CacheClearJobStatus.processing) {
      timer = setInterval(() => {
        if (userId) dispatch(getCacheClearJobStatus({ userId }));
      }, 10000);
    } else if (state.cacheClearJobStatus === CacheClearJobStatus.idle) {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    return () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, [dispatch, state.cacheClearJobStatus, userId]);

  const disabled = state.status !== SliceStatus.idle || state.cacheClearJobStatus !== CacheClearJobStatus.idle;

  return (
    <Fragment>
      <DeleteUrlsModal userId={userId} like={state.like} regex={state.regex} adaptiveType={state.adaptiveType} />
      <div className="card">
        <div className="card-header">
          <h4 className="card-header-title">Clear Cache</h4>
        </div>
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col">
              <div className="form-group">
                <label htmlFor="delete-url-like" className="form-label">
                  like (optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="delete-url-like"
                  value={state.like}
                  onChange={onLikeChange}
                />
              </div>
            </div>
            <div className="col">
              <div className="form-group">
                <label htmlFor="delete-url-regex" className="form-label">
                  regex (optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="delete-url-like"
                  value={state.regex}
                  onChange={onRegexChange}
                />
              </div>
            </div>
            <div className="col">
              <div className="form-group">
                <label htmlFor="delete-url-adaptiveType" className="form-label">
                  adaptive type
                </label>
                <select
                  id="delete-url-adaptiveType"
                  className="form-select mb-3"
                  data-choices
                  defaultValue={state.adaptiveType}
                  onChange={onAdaptiveTypeChanged}
                  disabled={true}
                >
                  <option>{AdaptiveType.all}</option>
                  <option>{AdaptiveType.desktop}</option>
                  <option>{AdaptiveType.mobile}</option>
                </select>
              </div>
            </div>
            <div className="col text-center">
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                show={state.cacheClearJobStatus === CacheClearJobStatus.processing}
                overlay={<Tooltip id="button-tooltip">A cache clear for this user is processing right now</Tooltip>}
              >
                <RequireTier requiredTier="admin" policy="disable">
                  <Button className="btn btn-primary" onClick={openClearCacheModal} disabled={disabled}>
                    Clear Cache...
                  </Button>
                </RequireTier>
              </OverlayTrigger>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DeleteUrls;
